import { FieldProps } from "formik";
import React, { useContext, useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import emojiSupport from "detect-emoji-support";
import PhoneInput from "react-phone-number-input/input";
import Form from "../..";
import ReactSelect from "react-select";
import UtilitiesContext from "../../../../Context/Utilities";
import { phone } from "phone";

export interface PhoneProps<T = any> {
	field?: FieldProps["field"];
	form?: FieldProps["form"];
	innerRef?: React.RefObject<T>;
	id: string;
	name: string;
	className?: string;
	autoComplete: boolean;
	defaultCountry?: string;
	setDefaultCountry?: React.Dispatch<React.SetStateAction<string>>;
}

export interface OptionType {
	value: string;
	label: string;
}

const getCountryCodeSafe = (sourceCountry: string | undefined) => {
	if (sourceCountry) {
		if (sourceCountry.length === 2) {
			return sourceCountry;
		} else if (sourceCountry.toLowerCase() === "united states") {
			return "US";
		}
	}
	return undefined;
};

const Phone: React.FC<PhoneProps> = ({
	field,
	form,
	className,
	id,
	name,
	innerRef,
	autoComplete,
	defaultCountry,
	setDefaultCountry,
}) => {
	const [countryMenuOpen, setCountryMenuOpen] = useState(false);
	const [country, setCountry] = useState(getCountryCodeSafe(defaultCountry) ?? "US");
	const [countrySearchValue, setCountrySearchValue] = useState("");

	useEffect(() => {
		if (field?.value) {
			const res = phone(field.value);
			if (res.isValid) {
				setCountry(res.countryIso2);
				form?.setFieldValue(field.name, res.phoneNumber);
			} else if (defaultCountry) {
				const countryRes = phone(field.value, { country: defaultCountry });
				if (countryRes.isValid) {
					setCountry(countryRes.countryIso2);
					form?.setFieldValue(field.name, countryRes.phoneNumber);
				}
			}
		}
	}, []);

	useEffect(() => {
		if (defaultCountry && !field?.value && !setDefaultCountry) {
			setCountry(getCountryCodeSafe(defaultCountry) ?? "US");
		}
	}, [defaultCountry]);

	const { localization } = useContext(UtilitiesContext);

	const data: OptionType[] = localization
		? localization.countryList.map((c) => ({
				value: c.code,
				label: c.name,
		  }))
		: [{ value: "US", label: "United States" }];

	const realValue = data.find((o) => o.value === country);

	//@ts-ignore
	const canUseEmojis = emojiSupport();
	return (
		<>
			<div
				className={`${className} country-region-picker m-0 ${countryMenuOpen ? "active" : ""}
									`}
			>
				<div className="field country-region-picker-country">
					<ReactSelect
						component={Form.Input.Select}
						id={`${name}-country`}
						name={`${name}-country`}
						//@ts-ignore
						getOptionLabel={(o: { value: string; label: string }) => (
							<span tabIndex={-1}>
								{canUseEmojis && (
									<span className="m-right-sm">
										<ReactCountryFlag countryCode={o.value} />
									</span>
								)}
								{countryMenuOpen && <>{o.label}</>}
							</span>
						)}
						options={data}
						inputProps={{ autoComplete: autoComplete ? "country" : "new-off" }}
						autoComplete={autoComplete ? "country" : "new-off"}
						placeholder="🏳️"
						value={realValue}
						isSearchable={true}
						className="react-select-container notranslate"
						classNamePrefix="react-select"
						onInputChange={(value: string) => {
							if (!countryMenuOpen) {
								const countryData = localization?.countryList.find(
									(c) =>
										c.code.toLowerCase() === value.toLowerCase() ||
										c.name.toLowerCase() === value.toLowerCase()
								);
								if (countryData) {
									setTimeout(() => {
										setCountrySearchValue("");
										setCountry(countryData.code);
										setCountryMenuOpen(false);
									}, 150);
								}
							}
							setCountrySearchValue(value);
						}}
						menuIsOpen={countryMenuOpen}
						onFocus={() => {
							setCountryMenuOpen(true);
						}}
						onBlur={() => {
							setCountryMenuOpen(false);
						}}
						onChange={(val) => {
							setCountry(getCountryCodeSafe(val?.value) ?? "");
							setCountrySearchValue("");
							setCountryMenuOpen(false);

							if (setDefaultCountry) {
								form?.setFieldValue("clientCountry", val?.value);
							}
						}}
						onMenuOpen={() => {
							form?.setStatus({ ...form.status, menuOpen: true });
						}}
						onMenuClose={() => {
							form?.setStatus({ ...form.status, menuOpen: false });
						}}
						tabSelectsValue={false}
						inputValue={countrySearchValue}
						filterOption={(
							{ value, data }: { label: string; value: string; data: { label: string; value: string } },
							search: string
						) => {
							if (search) {
								return (
									data.label.toLowerCase().includes(search.toLowerCase()) ||
									value
										?.toString()
										.toLowerCase()
										.includes(search.toLowerCase())
								);
							} else {
								return true;
							}
						}}
					/>
				</div>
				<div className="country-region-picker-divider"></div>
				<div className="field country-region-picker-region text-input">
					<PhoneInput
						ref={innerRef}
						id={id}
						name={(field && field.name) || name}
						value={(field && field.value) || ""}
						//@ts-ignore
						country={getCountryCodeSafe(country)}
						onChange={(value) => {
							if (field?.name.includes(".value")) {
								const target = (field && field.name) || name;
								const targetName = target?.substr(0, target.lastIndexOf("."));

								form?.setFieldValue((field && field.name) || name, value);
								//clear the confirm field if we edit the main field
								form?.setFieldValue(`${targetName}.confirmValue`, "");
							} else {
								form?.setFieldValue((field && field.name) || name, value);
							}
						}}
						placeholder={"Phone Number"}
						autoComplete={autoComplete ? "tel" : "new-off"}
					/>
				</div>
			</div>
		</>
	);
};

export default Phone;
